<script setup lang="ts">
import { ref } from 'vue';

import Modal from '../General/Modal.vue';
import Heading from '../General/Heading.vue';
import ButtonV2 from '../General/ButtonV2.vue';

interface ModalOptions {
    title: string
    text?: string
    style?: string
    confirmButtonText?: string
    cancelButtonText?: string
    reverse?: boolean
    confirmHandler?: () => void
    cancelHandler?: () => void
}
const props = defineProps<{
    darkMode?: boolean
}>()

const modalShow = ref<boolean>(false)
const modalOpts = ref<ModalOptions>()

function showModal(options: ModalOptions): Promise<void> {
    modalOpts.value = options;
    return new Promise((resolve, reject) => {
        modalShow.value = true;
        const confirmHandler = () => {
            modalShow.value = false;
            resolve();
        };
        const cancelHandler = () => {
            modalShow.value = false;
            reject();
        };
        modalOpts.value = {
            ...options,
            confirmHandler,
            cancelHandler,
        };
    });
}

// Expose the showModal function to the parent component
defineExpose({
    showModal,
});

</script>
<template>
    <Modal v-model="modalShow" :close-btn="false" :transparent="true">
        <div class="flex flex-col items-center border-2 border-yellow-500 dark:border-fuchsia-500 bg-white dark:bg-slate-800 text-indigo-950 dark:text-white rounded-3xl overflow-hidden"
            :data-mode="props?.darkMode ? 'dark' : 'light'">
            <Heading
                class="w-full text-center text-white !text-3xl font-bold bg-yellow-400 dark:bg-fuchsia-500 py-3 px-14 relative">
                <p class="capitalize">{{ modalOpts?.title }}</p>
                <div
                    class="absolute top-1/2 -translate-y-1/2 right-3 border-2 border-white bg-white dark:bg-fuchsia-500 rounded-xl h-fit w-fit">
                    <ButtonV2 type="icon" color="warning"
                        class="p-2 dark:bg-fuchsia-500 dark:shadow-fuchsia-700 dark:border-fuchsia-700"
                        @click="modalShow = false" @touchend="modalShow = false">
                        <svg width="20" height="20" viewBox="0 0 25 25" fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M17.0397 12.8086L23.2698 6.57859C24.5898 5.25859 24.5898 3.11859 23.2698 1.79859C21.9498 0.478594 19.8098 0.478594 18.4898 1.79859L12.2598 8.0286L6.02973 1.79859C4.70973 0.478594 2.56977 0.478594 1.24977 1.79859C-0.0702344 3.11859 -0.0702344 5.25859 1.24977 6.57859L7.47975 12.8086L1.24977 19.0386C-0.0702344 20.3586 -0.0702344 22.4986 1.24977 23.8186C2.56977 25.1386 4.70973 25.1386 6.02973 23.8186L12.2598 17.5886L18.4898 23.8186C19.8098 25.1386 21.9498 25.1386 23.2698 23.8186C24.5898 22.4986 24.5898 20.3586 23.2698 19.0386L17.0397 12.8086Z" />
                        </svg>
                    </ButtonV2>
                </div>
            </Heading>
            <div class="p-3 text-center" v-html="modalOpts?.text"></div>
            <div class="w-full flex justify-center gap-3 p-3" :class="{ 'flex-row-reverse': modalOpts?.reverse }">
                <ButtonV2 color="success" class="px-5" animation @click="modalOpts?.confirmHandler">
                    {{ modalOpts?.confirmButtonText }}
                </ButtonV2>
                <ButtonV2 color="danger" class="px-5" animation @click="modalOpts?.cancelHandler">
                    {{ modalOpts?.cancelButtonText }}
                </ButtonV2>
            </div>
        </div>
    </Modal>
</template>